/**
 * scene-doc.ts — build the public `scene.json` (matches
 * documentation/scene-schema.md, schemaVersion 1).
 *
 * This is the EDITOR → VIEWER contract. The editor's native zip+ssproj
 * format (doc.ts) is separate — that's for round-tripping editor state,
 * including pickerstate, color tints, etc. that the viewer doesn't need.
 *
 * The output here is intentionally minimal: only what the viewer can consume.
 * Unrecognized internal effects (alphaClip, minPixelSize, animated transform)
 * are skipped with a console warning rather than smuggled in — keeps the
 * contract honest and forces the user to ask for schema changes when they
 * want something extra.
 */

import type { Vec3 as PcVec3 } from 'playcanvas';

import { Element, ElementType } from '../element';
import type { Events } from '../events';
import type { Model } from './model';
import type { Scene } from '../scene';
import type { Splat } from '../splat';

import { SUPPORTED_SCHEMA_VERSION } from '../../../shared/scene-core/scene-schema';
import type {
    SceneFile, SplatElement, MeshElement, LightingConfig, Tonemap,
    CameraConfig, CameraInitial, CameraPose, NavigationMode, OrbitConfig, Vec3, Marker
} from '../../../shared/scene-core/scene-schema';

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const round = (v: number, dp = 4): number => {
    // Avoid runaway precision in JSON. 4 dp is enough for 0.1 mm on a 1m scene
    // and keeps file sizes sensible. Doesn't apply to keyframe values (those
    // round-trip through PropertyAnimTrack which already stores doubles).
    const m = Math.pow(10, dp);
    return Math.round(v * m) / m;
};

// pc.Vec3 → a rounded [x, y, z] tuple typed to the schema's Vec3. Replaces the
// old pack3().map(round) pattern, which produced an untyped number[].
const vec3 = (v: PcVec3): Vec3 => [round(v.x), round(v.y), round(v.z)];

const slugify = (s: string): string => {
    return (s ?? '')
    .normalize('NFKD')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'asset';
};

/**
 * Normalize an in-memory marker (Vec3 position) to the public JSON shape with a
 * plain [x,y,z] array. ONE definition, used by BOTH the public scene.json export
 * (scene-export.ts) and the editor's .ssproj/sidecar metadata (doc.ts) — so the
 * two serializations of marker data can't drift. The live `scene.markers` is the
 * canonical owner; this is just its projection.
 */
export const markerToJson = (m: any): Marker => ({
    id: m.id,
    position: [m.position.x, m.position.y, m.position.z],
    title: m.title,
    body: m.body
});

// ----------------------------------------------------------------------------
// Effect track → schema field mapper
// ----------------------------------------------------------------------------

/**
 * Read a uniform value off a splat material — used to bundle the
 * non-keyframed "config" params (radial center, end radius, color tints …)
 * alongside their parent effect's keyframe values.
 */
const readUniform = (el: Element, name: string, dim: number): number[] => {
    const mat: any = (el as Splat).entity?.gsplat?.instance?.material;
    const raw = mat?.parameters?.[name]?.data;
    if (raw === undefined || raw === null) return new Array(dim).fill(0);
    if (typeof raw === 'number') return [raw];
    const out: number[] = [];
    for (let i = 0; i < dim; i++) out.push(raw[i] ?? 0);
    return out;
};

/**
 * Snapshot the keys of an AnimTrack into `{ frame, value: number[] }[]` form.
 * PropertyAnimTrack.snapshot() returns that shape already; CameraAnimTrack
 * returns Pose[] so we handle it separately in buildCamera().
 */
const trackKeys = (track: any): { frame: number; value: number[] }[] => {
    if (!track || typeof track.snapshot !== 'function') return [];
    const snap = track.snapshot();
    if (!Array.isArray(snap)) return [];
    return snap
    .filter((f: any) => f && typeof f.frame === 'number' && Array.isArray(f.value))
    .map((f: any) => ({ frame: f.frame, value: f.value.slice() as number[] }));
};

/**
 * Convert an element's `tracks` map to the schema's `effects` block. Returns
 * an empty object when no keyframes (caller can drop the field).
 */
const effectsFromTracks = (el: Element): Record<string, any> => {
    const out: Record<string, any> = {};
    const tracks = (el as Element & { tracks?: Map<string, unknown> }).tracks;
    if (!tracks || tracks.size === 0) return out;

    for (const [id, track] of tracks.entries()) {
        const keys = trackKeys(track);
        if (keys.length === 0) continue;

        const valueFlat = (k: { frame: number; value: number[] }): number[] => [k.frame, ...k.value];

        switch (id) {
            // ---- Splat shader effects ----
            case 'splat-fade':
                out.fade = { values: keys.map(k => [k.frame, k.value[0]]) };
                break;
            case 'splat-dissolve':
                out.dissolve = { values: keys.map(k => [k.frame, k.value[0]]) };
                break;
            case 'splat-grow':
                out.growScale = { values: keys.map(k => [k.frame, k.value[0]]) };
                break;
            case 'splat-reveal-radius': {
                const block: any = {
                    values: keys.map(k => [k.frame, k.value[0]]),
                    center: readUniform(el, 'uRevealCenter', 3)
                };
                const fade = readUniform(el, 'uRevealFade', 1)[0];
                if (fade > 0) block.edgeSoftness = fade;
                out.revealRadius = block;
                break;
            }
            case 'splat-particle-reveal':
                out.particleReveal = {
                    progress:  keys.map(k => [k.frame, k.value[0]]),
                    spread:    readUniform(el, 'uParticleSpread', 1)[0],
                    direction: readUniform(el, 'uParticleDirection', 3),
                    duration:  readUniform(el, 'uParticleDuration', 1)[0],
                    flash:     readUniform(el, 'uParticleFlash', 1)[0]
                };
                break;
            case 'splat-radial-reveal':
                out.radialWave = {
                    time:         keys.map(k => [k.frame, k.value[0]]),
                    center:       readUniform(el, 'uRadialCenter', 3),
                    speed:        readUniform(el, 'uRadialSpeed', 1)[0],
                    acceleration: readUniform(el, 'uRadialAcceleration', 1)[0],
                    delay:        readUniform(el, 'uRadialDelay', 1)[0],
                    endRadius:    readUniform(el, 'uRadialEndRadius', 1)[0],
                    bandWidth:    readUniform(el, 'uRadialBandWidth', 1)[0],
                    oscillation:  readUniform(el, 'uRadialOscillation', 1)[0],
                    dotTint:      readUniform(el, 'uRadialDotTint', 3),
                    liftTint:     readUniform(el, 'uRadialLiftTint', 3)
                };
                break;

            // ---- Mesh effects ----
            case 'opacity':
                out.opacity = { values: keys.map(k => [k.frame, k.value[0]]) };
                break;
            case 'visible':
                out.visible = { values: keys.map(k => [k.frame, k.value[0]]) };
                break;
            case 'diffuse-color':
                out.diffuse = { color: keys.map(valueFlat) };
                break;
            case 'emissive-intensity':
                if (!out.emissive) out.emissive = {};
                out.emissive.intensity = keys.map(k => [k.frame, k.value[0]]);
                break;
            case 'emissive-color':
                if (!out.emissive) out.emissive = {};
                out.emissive.color = keys.map(valueFlat);
                break;
            case 'metalness':
                out.metalness = { values: keys.map(k => [k.frame, k.value[0]]) };
                break;
            case 'gloss':
                out.gloss = { values: keys.map(k => [k.frame, k.value[0]]) };
                break;
            case 'bumpiness':
                out.bumpiness = { values: keys.map(k => [k.frame, k.value[0]]) };
                break;
            case 'specular-intensity':
                out.specularity = { values: keys.map(k => [k.frame, k.value[0]]) };
                break;

            // ---- Internal-only effects (no schema slot) ----
            case 'splat-alpha':
            case 'splat-min-pixel-size':
                // Not in schema v1 — viewer can't consume. Drop silently
                // (these are editor-side tuning, not part of the public scene).
                break;

            // ---- Animated transforms — schema v1 has no slot for these ----
            case 'position':
            case 'rotation':
            case 'scale':
                console.warn(
                    `[scene-doc] element ${el.uid} has ${keys.length} '${id}' keyframes — not exported (scene schema v1 stores only initial transform). ` +
                    'If you need animated transforms, request a schema bump.'
                );
                break;

            default:
                console.warn(`[scene-doc] unknown effect id '${id}' on element ${el.uid} — skipped`);
        }
    }

    return out;
};

// ----------------------------------------------------------------------------
// Compose pipeline → lighting block
// ----------------------------------------------------------------------------


const buildLighting = (scene: Scene): LightingConfig | undefined => {
    // authored in ollo/lighting.ts; undefined when neutral so the viewer keeps its passthrough
    const ev = scene.events;
    if (!ev?.functions?.has('lighting.sceneConfig')) return undefined;
    return (ev.invoke('lighting.sceneConfig') ?? undefined) as LightingConfig | undefined;
};

// ----------------------------------------------------------------------------
// Camera block (initial pose + optional pose track)
// ----------------------------------------------------------------------------

const buildCamera = (scene: Scene, events: Events, navigation?: NavigationMode): CameraConfig => {
    // Prefer an explicit "starting view" the author set via the View panel;
    // fall back to the live editor viewport when none is set (historical
    // behaviour). The stored pose uses [x,y,z] arrays — reshape to the
    // {x,y,z} form the rest of this function reads.
    const stored = events.invoke('camera.getStartPose') as
        { position: number[], target: number[], fov: number } | null | undefined;
    const pose = stored
        ? {
            position: { x: stored.position[0], y: stored.position[1], z: stored.position[2] },
            target: { x: stored.target[0], y: stored.target[1], z: stored.target[2] },
            fov: stored.fov
        }
        : (events.invoke('camera.getPose') ?? null);
    const initial: CameraInitial = {
        position: pose
            ? [round(pose.position.x), round(pose.position.y), round(pose.position.z)]
            : [3, 1, 0.5],
        target: pose
            ? [round(pose.target.x), round(pose.target.y), round(pose.target.z)]
            : [0, 0, 0],
        fov: pose?.fov ?? scene.camera.fov
    };
    // Flag a deliberately-authored starting view so the viewer honours its
    // look-target in every nav mode (orbit included). A plain live-viewport
    // fallback stays unflagged → orbit keeps spinning around the splat centre.
    if (stored) initial.explicit = true;

    // Rotating scenes ALWAYS orbit the world origin — the author centers the
    // property on the grid-axes crossing, and the zoom band / pitch floor are
    // measured around it. The start view keeps its camera POSITION (framing)
    // but the look-target is pinned to the pivot, which also prevents the
    // first-drag snap when position and pivot disagree.
    if (navigation === 'rotating') {
        initial.target = [0, 0, 0];
        initial.explicit = true;
    }

    const out: CameraConfig = { initial };

    const track: any = events.invoke('camera.animTrack');
    const poses = (typeof track?.getPoses === 'function') ? track.getPoses() : [];
    if (poses && poses.length > 0) {
        out.poses = poses.map((p: any): CameraPose => ({
            frame: p.frame,
            position: [round(p.position.x), round(p.position.y), round(p.position.z)],
            target:   [round(p.target.x),   round(p.target.y),   round(p.target.z)],
            fov: p.fov ?? initial.fov
        }));
    }

    return out;
};

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

export interface BuildSceneJsonOptions {
    /** Per-element override of the URL written into scene.json. The exporter
     *  uses this to thread the actual chosen filename in the bundle (so the
     *  viewer can fetch it from the same folder as scene.json). */
    splatUrls?: Map<number, string>;
    modelUrls?: Map<number, string>;
    /** Display name for the scene. Defaults to events.invoke('doc.name') or 'untitled'. */
    name?: string;
    /** Navigation mode. Defaults to 'free' (cursor-look + WASD); set 'rotating' for orbit. */
    navigation?: NavigationMode;
    /** Per-scene orbit-camera behavior (pitch floor / zoom band / idle
     *  auto-rotate). Emitted as camera.orbit; the viewer applies its polished
     *  rotating-scene defaults when absent. */
    orbit?: OrbitConfig;
    /** If set, an `hdri` block referencing this URL is emitted. */
    hdriUrl?: string;
    showSkybox?: boolean;
    /** Optional markers — passed through from scene.markers.serialize() with
     *  the Vec3 positions normalized to plain [x,y,z] arrays.  Empty arrays
     *  are dropped (no `markers` key written). */
    markers?: any[];
    /** Cover thumbnail URL within the bundle (e.g. "thumbnail.jpg"). Emitted as
     *  the top-level `thumbnail` field when a thumbnail has been captured. */
    thumbnailUrl?: string;
    /** Preview/hover video URL within the bundle (e.g. "preview.mp4"). Emitted as
     *  the top-level `previewVideo` field when a preview has been captured. */
    previewVideoUrl?: string;
}

/**
 * Build the schema-v1 scene.json object for the current scene state.
 *
 * Pure-ish: doesn't mutate scene/events. The caller is responsible for
 * choosing the splat / model URLs (which depend on how the bundle is
 * packaged) and passing them in via options. If you omit them, sensible
 * filename defaults are generated from the element name.
 */
export const buildSceneJson = (
    scene: Scene,
    events: Events,
    opts: BuildSceneJsonOptions = {}
): SceneFile => {
    const splatEls = scene.getElementsByType(ElementType.splat) as Splat[];
    const modelEls = scene.getElementsByType(ElementType.model) as Model[];

    const splats: SplatElement[] = splatEls.map((s, i) => {
        const id = ((s as any).name && slugify((s as any).name)) || `splat-${i}`;
        const url = opts.splatUrls?.get(s.uid) ?? `${id}.ply`;
        const block: SplatElement = {
            id,
            url,
            transform: {
                position: vec3(s.entity.getLocalPosition()),
                rotation: vec3(s.entity.getLocalEulerAngles()),
                scale:    vec3(s.entity.getLocalScale())
            }
        };
        const fx = effectsFromTracks(s);
        if (Object.keys(fx).length > 0) block.effects = fx;
        return block;
    });

    const models: MeshElement[] = modelEls.map((m, i) => {
        const id = (m.name && slugify(m.name)) || `model-${i}`;
        const url = opts.modelUrls?.get(m.uid) ?? `${id}.glb`;
        const block: MeshElement = {
            id,
            url,
            transform: {
                position: vec3(m.entity.getLocalPosition()),
                rotation: vec3(m.entity.getLocalEulerAngles()),
                scale:    vec3(m.entity.getLocalScale())
            }
        };
        const fx = effectsFromTracks(m);
        if (Object.keys(fx).length > 0) block.effects = fx;
        return block;
    });

    // Navigation mode. 'free' (cursor-look + WASD walk) is the intended default
    // experience for splat demos — Olloport scenes are spaces you walk through,
    // not objects you orbit. Orbit ('rotating') is opt-in via opts.navigation.
    // viewer2 honours 'free' via its WalkController when the scene carries a
    // `walk` block (floor + ≥3 boundary points); without one it falls back to orbit.
    const navigation: NavigationMode = opts.navigation ?? 'free';

    const doc: SceneFile = {
        schemaVersion: SUPPORTED_SCHEMA_VERSION,
        name: opts.name ?? (events.invoke('doc.name') as string) ?? 'untitled',
        navigation,
        splats,
        models,
        camera: buildCamera(scene, events, navigation)
    };

    // Per-scene orbit behavior (pitch floor / zoom band / idle auto-rotate).
    if (opts.orbit && Object.keys(opts.orbit).length > 0 && doc.camera) {
        doc.camera.orbit = opts.orbit;
    }

    const lighting = buildLighting(scene);
    if (lighting) doc.lighting = lighting;

    if (opts.hdriUrl) {
        doc.hdri = {
            url: opts.hdriUrl,
            showSkybox: opts.showSkybox ?? false
        };
    }

    if (opts.markers && opts.markers.length > 0) doc.markers = opts.markers;

    // Cover thumbnail (a bundled image, e.g. "thumbnail.jpg"). Metadata for a
    // future scene list; the viewer ignores it. Omitted when none captured.
    if (opts.thumbnailUrl) doc.thumbnail = opts.thumbnailUrl;
    if (opts.previewVideoUrl) doc.previewVideo = opts.previewVideoUrl;

    // Walking path (floor plane + walkable boundary). Omitted when not authored
    // (no plane or <3 boundary points). See documentation/scene-schema.md.
    const walk = (events.functions.has('walk.serialize') ? events.invoke('walk.serialize') : null) ?? null;
    if (walk) doc.walk = walk;

    return doc;
};

export { slugify };
